import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

const TOKEN_STORAGE_KEY = "dotperson:connect-admin-token";

interface ConnectionStatus {
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
}

type Banner =
  | { kind: "connected" }
  | { kind: "denied" }
  | { kind: "state_invalid" }
  | { kind: "no_refresh_token" }
  | { kind: "error" }
  | null;

function bannerFromQuery(): Banner {
  const sp = new URLSearchParams(window.location.search);
  const v = sp.get("gmail");
  switch (v) {
    case "connected":
      return { kind: "connected" };
    case "denied":
      return { kind: "denied" };
    case "state_invalid":
      return { kind: "state_invalid" };
    case "no_refresh_token":
      return { kind: "no_refresh_token" };
    case "error":
      return { kind: "error" };
    default:
      return null;
  }
}

function readInitialToken(): string {
  // Order: ?token=... in URL → localStorage → empty
  if (typeof window === "undefined") return "";
  const sp = new URLSearchParams(window.location.search);
  const fromUrl = sp.get("token");
  if (fromUrl) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, fromUrl);
    // Strip the token from the visible URL once captured.
    const cleaned = new URL(window.location.href);
    cleaned.searchParams.delete("token");
    window.history.replaceState({}, "", cleaned.toString());
    return fromUrl;
  }
  return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
}

export function Connect() {
  const [token, setToken] = React.useState<string>(() => readInitialToken());
  const [status, setStatus] = React.useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [banner] = React.useState<Banner>(() => bannerFromQuery());

  const refresh = React.useCallback(async (t: string) => {
    if (!t) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/admin/connection/status?token=${encodeURIComponent(t)}`
      );
      if (res.status === 401) {
        setStatus(null);
        setError("Token rejected. Re-enter the connect admin token.");
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        return;
      }
      if (!res.ok) {
        throw new Error(`status ${res.status}`);
      }
      const data = (await res.json()) as ConnectionStatus;
      setStatus(data);
    } catch (err) {
      setError(`Could not read status: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refresh(token);
  }, [token, refresh]);

  const handleSetToken = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const t = String(fd.get("token") ?? "").trim();
    window.localStorage.setItem(TOKEN_STORAGE_KEY, t);
    setToken(t);
  };

  const handleConnect = () => {
    window.location.href = `/api/admin/auth/google?token=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Workspace?")) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/admin/auth/google/disconnect?token=${encodeURIComponent(token)}`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`status ${res.status}`);
      await refresh(token);
    } catch (err) {
      setError(`Could not disconnect: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Seo
        title="Admin · Connect — The .person Foundation"
        description="One-time Google Workspace connection for dotperson.org outbound email."
        path="/admin/connect"
        noindex
      />
      <div className="max-w-xl mx-auto py-16 px-6">
        <p className="font-mono text-[10px] text-ink-2 uppercase tracking-widest mb-3">
          Admin · One-time setup
        </p>
        <h1 className="font-serif text-3xl text-ink mb-6">
          Connect Google Workspace
        </h1>
        <p className="font-serif text-base text-ink-2 leading-relaxed mb-8">
          Authorise dotperson.org to send outbound mail (subscribe and contact
          acknowledgements, internal notifications) through your connected
          Workspace mailbox. The connection is single-tenant and persisted in
          Firestore. Disconnect at any time.
        </p>

        {banner?.kind === "connected" && (
          <div className="mb-8 border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 font-mono text-xs text-emerald-300">
            Google Workspace connected.
          </div>
        )}
        {banner?.kind === "denied" && (
          <div className="mb-8 border border-amber-500/40 bg-amber-500/5 px-4 py-3 font-mono text-xs text-amber-300">
            Consent was declined. No connection saved.
          </div>
        )}
        {banner?.kind === "state_invalid" && (
          <div className="mb-8 border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-300">
            OAuth state was invalid or expired. Try again.
          </div>
        )}
        {banner?.kind === "no_refresh_token" && (
          <div className="mb-8 border border-amber-500/40 bg-amber-500/5 px-4 py-3 font-mono text-xs text-amber-300">
            Google did not return a refresh token. Disconnect first, then
            reconnect — the OAuth client must be set to prompt for consent.
          </div>
        )}
        {banner?.kind === "error" && (
          <div className="mb-8 border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-300">
            OAuth callback failed. Check function logs and try again.
          </div>
        )}

        {!token && (
          <form onSubmit={handleSetToken} className="space-y-4 mb-10">
            <label
              htmlFor="connect-token"
              className="block font-mono text-[10px] text-ink-2 uppercase tracking-widest"
            >
              Connect admin token
            </label>
            <input
              id="connect-token"
              name="token"
              type="password"
              autoComplete="off"
              className="w-full bg-bg border border-ink-3/30 px-4 py-3 font-mono text-sm text-ink focus:outline-none focus:border-ink"
              placeholder="paste CONNECT_ADMIN_TOKEN"
              required
            />
            <button
              type="submit"
              className="w-full bg-ink text-bg font-mono text-xs uppercase tracking-widest py-3"
            >
              Continue
            </button>
            <p className="font-mono text-[10px] text-ink-3">
              Token is stored locally in this browser only.
            </p>
          </form>
        )}

        {token && (
          <div className="space-y-6">
            {loading && (
              <p className="font-mono text-xs text-ink-2">Loading…</p>
            )}
            {error && (
              <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-300">
                {error}
              </div>
            )}

            {status && status.connected ? (
              <div className="space-y-4">
                <div className="border border-ink-3/30 px-4 py-4">
                  <p className="font-mono text-[10px] text-ink-2 uppercase tracking-widest mb-2">
                    Connected
                  </p>
                  <p className="font-serif text-lg text-ink">
                    {status.email ?? "(unknown mailbox)"}
                  </p>
                  {status.connectedAt && (
                    <p className="font-mono text-[10px] text-ink-3 mt-1">
                      since {new Date(status.connectedAt).toISOString()}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="w-full border border-red-500/40 text-red-300 font-mono text-xs uppercase tracking-widest py-3 hover:bg-red-500/10 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
            ) : status && !status.connected ? (
              <div className="space-y-4">
                <p className="font-serif text-base text-ink-2 leading-relaxed">
                  No Workspace mailbox connected. Connecting will redirect you
                  to Google's consent screen.
                </p>
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={loading}
                  className="w-full bg-ink text-bg font-mono text-xs uppercase tracking-widest py-3 disabled:opacity-50"
                >
                  Connect Google Workspace
                </button>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => {
                window.localStorage.removeItem(TOKEN_STORAGE_KEY);
                setToken("");
                setStatus(null);
                setError(null);
              }}
              className="w-full font-mono text-[10px] text-ink-3 underline"
            >
              Forget token in this browser
            </button>
          </div>
        )}

        <p className="mt-12 font-mono text-[10px] text-ink-3">
          <Link to="/" className="underline">
            ← back to dotperson.org
          </Link>
        </p>
      </div>
    </PageShell>
  );
}
